import React from 'react';
import { motion } from 'framer-motion';
import { SmartphoneIcon, MapIcon, TrendingUpIcon, ArrowRightIcon, LayersIcon, ZapIcon, ShieldCheckIcon, GraduationCapIcon, RouteIcon, SparklesIcon, CheckIcon } from 'lucide-react';
import { Header } from '../Header';
import { SiteFooter } from '../SiteFooter';
import { FadeIn } from '../ui';
export function PhunwarePage() {
  return <div className="min-h-screen bg-white font-sans text-slate-800 flex flex-col" data-id="element-4901">
      <Header data-id="element-4902" />

      <main className="flex-1" data-id="element-4903">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-[#0A0F1F]" data-id="element-4904">
          {/* Ambient background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-4905">
            <motion.div animate={{
            x: [0, 30, 0],
            y: [0, -20, 0]
          }} transition={{
            duration: 14,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.15] rounded-full blur-[100px]" data-id="element-4906" />
            <motion.div animate={{
            x: [0, -25, 0],
            y: [0, 30, 0]
          }} transition={{
            duration: 16,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-1/4 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.12] rounded-full blur-[100px]" data-id="element-4907" />
          </div>

          {/* Grid texture */}
          <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
        }} data-id="element-4908" />

          <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10 text-center" data-id="element-4909">
            <FadeIn data-id="element-4910">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-2 pr-4 py-1.5 mb-8 backdrop-blur-sm" data-id="element-4911">
                <span className="bg-brand-teal text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4912">
                  Integration Partner
                </span>
                <span className="text-sm font-medium text-slate-300" data-id="element-4913">
                  Offsite+ &amp; Phunware
                </span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-4914">
              <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight text-white mb-6 text-balance" data-id="element-4915">
                Powering the next generation of{' '}
                <span className="text-brand-coral" data-id="element-4916">group travel</span> and
                corporate offsites.
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-4917">
              <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed text-balance max-w-3xl mx-auto" data-id="element-4918">
                Combine Phunware's industry-leading mobile hospitality platform
                with Offsite+'s AI-powered group intelligence. Deliver seamless
                wayfinding, personalized learning pathways, and a unified
                revenue engine right to your guests' devices.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-4919">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-4920">
                <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed no-underline bg-brand-teal text-white hover:bg-teal-500 focus:ring-brand-teal shadow-lg shadow-brand-teal/20 text-base px-8 py-4 group w-full sm:w-auto" data-id="element-4921">
                  Book an Integration Briefing
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4922" />
                </a>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Value Props */}
        <section className="py-24 bg-white" data-id="element-4923">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-4924">
            <FadeIn data-id="element-4925">
              <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-4926">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-4927">
                  Two powerful platforms. One seamless experience.
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed" data-id="element-4928">
                  We've brought the best of Offsite+'s group management and
                  revenue tools directly into the Phunware mobile ecosystem.
                </p>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-4929">
              <FadeIn delay={0.1} data-id="element-4930">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4931">
                  <div className="w-14 h-14 bg-brand-navy/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4932">
                    <SmartphoneIcon className="w-7 h-7 text-brand-navy" data-id="element-4933" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4934">
                    Seamless Guest Experience
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4935">
                    Attendees access their personalized Offsite+ agendas,
                    learning pathways, and networking groups directly within the
                    Phunware-powered hotel app. No secondary downloads required.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4936">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4937">
                  <div className="w-14 h-14 bg-brand-teal/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4938">
                    <MapIcon className="w-7 h-7 text-brand-teal" data-id="element-4939" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4940">
                    Location-Aware Networking
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4941">
                    Combine Phunware's indoor wayfinding and location services
                    with Offsite+'s smart networking. Guide attendees to their
                    breakout sessions or pod meetups effortlessly.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.3} data-id="element-4942">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4943">
                  <div className="w-14 h-14 bg-brand-coral/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4944">
                    <TrendingUpIcon className="w-7 h-7 text-brand-coral" data-id="element-4945" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4946">
                    Unified Revenue Engine
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4947">
                    Drive F&amp;B, spa, and activity bookings through
                    personalized, context-aware prompts delivered at exactly the
                    right moment during the offsite schedule.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Technical Integration */}
        <section className="py-24 bg-slate-50 border-t border-slate-100" data-id="element-4948">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-4949">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-4950">
              <FadeIn data-id="element-4951">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6 tracking-tight" data-id="element-4952">
                  Deep technical synergy for hospitality leaders.
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-4953">
                  The integration is built on robust APIs that sync data in real
                  time, ensuring that property operators have a single source of
                  truth while guests enjoy a frictionless digital journey.
                </p>

                <ul className="space-y-6" data-id="element-4954">
                  {[{
                  icon: LayersIcon,
                  title: 'Native SDK Integration',
                  desc: 'Embed Offsite+ modules natively within your existing Phunware application.'
                }, {
                  icon: ZapIcon,
                  title: 'Real-Time Sync',
                  desc: 'Agenda changes, room updates, and personalized prompts sync instantly across platforms.'
                }, {
                  icon: ShieldCheckIcon,
                  title: 'Enterprise Security',
                  desc: 'Bank-grade encryption and strict data privacy controls protect corporate client information.'
                }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-4955">
                      <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-4956">
                        <item.icon className="w-5 h-5 text-brand-teal" data-id="element-4957" />
                      </div>
                      <div data-id="element-4958">
                        <h4 className="text-base font-bold text-brand-navy mb-1" data-id="element-4959">
                          {item.title}
                        </h4>
                        <p className="text-sm text-slate-600 leading-relaxed" data-id="element-4960">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4961">
                <div className="relative" data-id="element-4962">
                  <div className="absolute inset-0 bg-gradient-to-tr from-brand-teal/20 to-brand-coral/20 rounded-3xl blur-3xl transform rotate-3" data-id="element-4963" />
                  <div className="relative bg-white border border-slate-200 rounded-3xl p-8 shadow-xl" data-id="element-4964">
                    <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-100" data-id="element-4965">
                      <div className="font-bold text-xl text-brand-navy" data-id="element-4966">
                        Phunware App
                      </div>
                      <div className="flex gap-2" data-id="element-4967">
                        <span className="w-3 h-3 rounded-full bg-slate-200" data-id="element-4968" />
                        <span className="w-3 h-3 rounded-full bg-slate-200" data-id="element-4969" />
                        <span className="w-3 h-3 rounded-full bg-slate-200" data-id="element-4970" />
                      </div>
                    </div>

                    <div className="space-y-4" data-id="element-4971">
                      <div className="h-24 bg-slate-50 rounded-xl border border-slate-100 flex items-center px-6 gap-4" data-id="element-4972">
                        <div className="w-12 h-12 rounded-full bg-brand-teal/10 flex items-center justify-center" data-id="element-4973">
                          <MapIcon className="w-6 h-6 text-brand-teal" data-id="element-4974" />
                        </div>
                        <div data-id="element-4975">
                          <div className="h-4 w-32 bg-slate-200 rounded mb-2" data-id="element-4976" />
                          <div className="h-3 w-48 bg-slate-100 rounded" data-id="element-4977" />
                        </div>
                      </div>

                      {/* Offsite+ Module Simulation */}
                      <div className="p-6 bg-brand-navy rounded-xl text-white relative overflow-hidden" data-id="element-4978">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-coral/20 rounded-full blur-2xl transform translate-x-1/2 -translate-y-1/2" data-id="element-4979" />
                        <div className="flex items-center gap-2 mb-4" data-id="element-4980">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-brand-coral" data-id="element-4981">
                            Powered by Offsite+
                          </span>
                        </div>
                        <div className="text-lg font-bold mb-2" data-id="element-4982">
                          Leadership Retreat Agenda
                        </div>
                        <div className="text-sm text-slate-300 mb-4" data-id="element-4983">
                          Your personalized learning pathway is ready.
                        </div>
                        <div className="h-10 bg-white/10 rounded-lg flex items-center px-4 backdrop-blur-sm border border-white/5" data-id="element-4984">
                          <span className="text-sm font-medium" data-id="element-4985">
                            View Schedule
                          </span>
                          <ArrowRightIcon className="w-4 h-4 ml-auto" data-id="element-4986" />
                        </div>
                      </div>

                      <div className="h-24 bg-slate-50 rounded-xl border border-slate-100 flex items-center px-6 gap-4" data-id="element-4987">
                        <div className="w-12 h-12 rounded-full bg-brand-coral/10 flex items-center justify-center" data-id="element-4988">
                          <SmartphoneIcon className="w-6 h-6 text-brand-coral" data-id="element-4989" />
                        </div>
                        <div data-id="element-4990">
                          <div className="h-4 w-24 bg-slate-200 rounded mb-2" data-id="element-4991" />
                          <div className="h-3 w-40 bg-slate-100 rounded" data-id="element-4992" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Where we draw the line */}
        <section className="py-24 bg-white border-t border-slate-100" data-id="element-4993">
          <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-4994">
            <FadeIn data-id="element-4995">
              <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-4996">
                <div className="inline-flex items-center gap-2 bg-brand-navy/5 border border-brand-navy/10 rounded-full px-3 py-1 mb-6" data-id="element-4997">
                  <span className="text-xs font-bold text-brand-navy uppercase tracking-wider" data-id="element-4998">
                    Complementary, not competing
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-4999">
                  We don't replace Phunware — we run inside it.
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed" data-id="element-5000">
                  Phunware owns the mobile platform. Offsite Plus owns the
                  programming that makes it worth opening. There is zero
                  overlap.
                </p>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-2 gap-6 mb-16" data-id="element-5001">
              <FadeIn delay={0.1} data-id="element-5002">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full" data-id="element-5003">
                  <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-5" data-id="element-5004">
                    Phunware provides
                  </div>
                  <ul className="space-y-3" data-id="element-5005">
                    {['The branded native mobile app & shell', 'Indoor wayfinding & location services', 'Push notifications & engagement delivery', 'Audience data & app analytics'].map(t => <li key={t} className="flex items-start gap-3" data-id="element-5006">
                        <CheckIcon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-5007" />
                        <span className="text-slate-600" data-id="element-5008">{t}</span>
                      </li>)}
                  </ul>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-5009">
                <div className="bg-brand-navy rounded-2xl p-8 h-full text-white relative overflow-hidden" data-id="element-5010">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/20 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3" data-id="element-5011" />
                  <div className="relative" data-id="element-5012">
                    <div className="text-sm font-bold text-brand-teal uppercase tracking-wider mb-5" data-id="element-5013">
                      Offsite Plus provides
                    </div>
                    <ul className="space-y-3" data-id="element-5014">
                      {['AI-built customized learning pathways per group', 'Personalized session & content sequencing', 'Group programming, prompts & reflection moments', 'Measurable learning outcomes & revenue lift'].map(t => <li key={t} className="flex items-start gap-3" data-id="element-5015">
                          <CheckIcon className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" data-id="element-5016" />
                          <span className="text-slate-200" data-id="element-5017">{t}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Customized learning pathways — our lane */}
            <FadeIn delay={0.15} data-id="element-5018">
              <div className="bg-slate-50 rounded-3xl border border-slate-100 p-8 md:p-12" data-id="element-5019">
                <div className="max-w-3xl mb-12" data-id="element-5020">
                  <div className="inline-flex items-center gap-2 bg-brand-coral/10 rounded-full px-3 py-1 mb-5" data-id="element-5021">
                    <SparklesIcon className="w-3.5 h-3.5 text-brand-coral" data-id="element-5022" />
                    <span className="text-xs font-bold text-brand-coral uppercase tracking-wider" data-id="element-5023">
                      Our clear pathway
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-5024">
                    Customized learning pathways are what we do.
                  </h3>
                  <p className="text-lg text-slate-600 leading-relaxed" data-id="element-5025">
                    Phunware gets the app onto the guest's phone. We make sure
                    that when they open it, they find a learning journey built
                    specifically for them — not a generic agenda. For every
                    corporate group, our AI assembles a unique pathway from each
                    attendee's role, goals, and the client's objectives, then
                    sequences sessions, content, and reflection prompts so the
                    program actually lands and drives repeat, higher-value
                    bookings.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6" data-id="element-5026">
                  {[{
                  icon: RouteIcon,
                  title: 'Built per group, per person',
                  desc: 'Each attendee gets a pathway mapped to their role, seniority, and the offsite goals — no two are identical.'
                }, {
                  icon: GraduationCapIcon,
                  title: 'Sequenced for retention',
                  desc: 'Sessions, micro-content, and reflection prompts are ordered so learning compounds across the days.'
                }, {
                  icon: TrendingUpIcon,
                  title: 'Tied to measurable outcomes',
                  desc: 'Completion, engagement, and before/after shifts prove ROI and justify premium group pricing.'
                }].map((item, i) => <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100" data-id="element-5027">
                      <div className="w-11 h-11 rounded-xl bg-brand-teal/10 flex items-center justify-center mb-4" data-id="element-5028">
                        <item.icon className="w-5 h-5 text-brand-teal" data-id="element-5029" />
                      </div>
                      <h4 className="text-base font-bold text-brand-navy mb-2" data-id="element-5030">
                        {item.title}
                      </h4>
                      <p className="text-sm text-slate-600 leading-relaxed" data-id="element-5031">
                        {item.desc}
                      </p>
                    </div>)}
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-white text-center" data-id="element-5032">
          <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-5033">
            <FadeIn data-id="element-5034">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6" data-id="element-5035">
                Ready to upgrade your property's digital experience?
              </h2>
              <p className="text-lg text-slate-600 mb-10" data-id="element-5036">
                Let's discuss how the Offsite+ and Phunware integration can
                drive more revenue and better engagement for your corporate
                groups.
              </p>
              <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 no-underline bg-brand-navy text-white hover:bg-slate-800 focus:ring-brand-navy shadow-lg text-base px-8 py-4 group" data-id="element-5037">
                Schedule a Demo
                <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-5038" />
              </a>
            </FadeIn>
          </div>
        </section>
      </main>

      <SiteFooter data-id="element-5039" />
    </div>;
}